<template>
  <div class="mobileTimeTracking">
    <div class="grid">
      <Card>
        <template #title>
          <div class="mobileTimeTracking" v-if="appointment && appointment.project">
            {{ $t('time_tracking') }} bei {{ appointment.project.customer.firstname }}
            {{ appointment.project.customer.lastname }}
          </div>
        </template>

        <template #content>
          <div class="time-display">{{ duration }}</div>
        </template>
        <template #footer>
          <template>
            <Button
              class="btn btn-narrow btn-inverse ml-2 mb-2"
              @click="toggleTimeTracking"
              :label="timeTrackingState === 'STARTED' ? $t('stop') : $t('start')"
            >
            </Button>
          </template>
        </template>
      </Card>
      <Card>
        <template #title>
          Meine Zeiten
        </template>
        <template #content> <MobileTimeTrackingTable /> </template>
      </Card>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Card from 'primevue/card';
import Button from 'primevue/button';
import MobileTimeTrackingTable from '@/components/MobileTimeTrackingTable';

export default {
  name: 'MobileTimeTracking',
  components: {
    Card,
    Button,
    MobileTimeTrackingTable,
  },
  props: {
    project: { type: Object },
    appointment: { type: Object },
  },
  data() {
    return {
      startTime: null,
      stopTime: null,
      currentTime: null,
      timer: null,
      timeTrackingNumber: null,
      timeTrackingState: null,
    };
  },
  computed: {
    ...mapGetters(['getTimeTrackings', 'getTimeTrackingByNumber']),

    duration() {
      if (!this.startTime) return '00:00:00';

      const start = new Date(this.startTime).getTime();
      const end = new Date(this.currentTime).getTime();
      const diff = Math.floor((end - start) / 1000);

      const hours = String(Math.floor(diff / 3600)).padStart(2, '0');
      const minutes = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
      const seconds = String(diff % 60).padStart(2, '0');

      return `${hours}:${minutes}:${seconds}`;
    },
  },
  created() {
    this.loadTimerState();
    this.timeTrackingState = localStorage.getItem('timeTrackingState') || 'STOPPED';
  },
  methods: {
    ...mapActions(['startTimeTracking', 'stopTimeTracking']),
    toggleTimeTracking() {
      if (this.timeTrackingState === 'STARTED') {
        this.stop();
      } else {
        this.start();
      }
    },
    loadTimerState() {
      const storedStartTime = localStorage.getItem('startTime');
      const storedStatus = localStorage.getItem('timeTrackingState');
      const storedTimeTrackingNumber = localStorage.getItem('timeTrackingNumber');

      if (storedStartTime && storedStatus === 'STARTED') {
        this.startTime = storedStartTime;
        this.currentTime = new Date().toISOString();
        this.timeTrackingNumber = storedTimeTrackingNumber;
        this.startTimer();
      }
    },

    startTimer() {
      this.timer = setInterval(() => {
        this.currentTime = new Date().toISOString();
      }, 1000);
    },

    async start() {
      const response = await this.startTimeTracking({
        project: this.project,
        appointment: this.appointment,
      });

      // Use server startTime if available, otherwise fallback to now
      const now = new Date();
      now.setMilliseconds(0);
      this.startTime = response?.startTime || now.toISOString();
      this.currentTime = now.toISOString();
      this.timeTrackingState = 'STARTED';

      // persist local state
      localStorage.setItem('startTime', this.startTime);
      localStorage.setItem('timeTrackingState', 'STARTED');
      localStorage.removeItem('stopTime');
      this.startTimer();

      this.timeTrackingNumber = response?.number || null;
      if (this.timeTrackingNumber) {
        localStorage.setItem('timeTrackingNumber', this.timeTrackingNumber);
      }
    },

    async stop() {
      try {
        const stopped = await this.stopTimeTracking();
        const stopTime = stopped?.stopTime || new Date().toISOString();
        this.stopTime = stopTime;
        this.updateTimeTrackingState('STOPPED', stopTime);
        clearInterval(this.timer);
        this.timeTrackingState = 'STOPPED';
      } catch (e) {
        // optional toast could be added here
      }
    },
    updateTimeTrackingState(state, stopTime = null) {
      localStorage.setItem('timeTrackingState', state);
      if (stopTime) {
        localStorage.setItem('stopTime', stopTime);
      } else {
        localStorage.removeItem('stopTime');
      }

      if (state === 'STOPPED') {
        localStorage.removeItem('timeTrackingNumber');
      }
    },
  },
};
</script>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(600px, 1fr));
  gap: 0.5em;
}
.grid > div {
  padding: 1em;
  border: 2px;
}

.time-display {
  font-size: 2rem;
  margin-bottom: 1rem;
}
.button-container {
  display: flex;
  justify-content: center;
}
</style>
